import sharedSupabase from '../config/supabase';

/**
 * Item Alias Service (web)
 *
 * Data access for public.item_aliases — known alternate names/SKUs that point
 * at a canonical inventory item ("the same product, called something else by
 * this vendor"). fetchAliasMap loads a store's aliases as one in-memory map
 * so import/counting flows can resolve vendor wording to items without a
 * round-trip per line; matchAliases does the pure lookup; addAlias records a
 * newly learned alias.
 *
 * The item_aliases table ships in a migration that may not be deployed
 * everywhere yet — fetchAliasMap degrades gracefully (console.warn + {}) when
 * the schema is missing instead of crashing the UI.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const ALIAS_COLUMNS = 'id, inventory_item_id, alias, normalized_alias, vendor_id';

const throwIfError = (error) => {
  if (error) throw new Error(error.message);
};

// PostgREST/Postgres "not deployed yet" signatures: function or table missing
// from the schema cache (PGRST202/PGRST205) or from the database itself
// (42883 undefined_function / 42P01 undefined_table).
const isMissingSchemaError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return (
    ['PGRST202', 'PGRST205', '42883', '42P01'].includes(code) ||
    /could not find the (function|table)|schema cache|does not exist/i.test(message)
  );
};

// Canonical form of an alias or query: case- and whitespace-insensitive, so
// 'Whole  MILK ' and 'whole milk' collide on the same map key.
const normalizeAlias = (value) =>
  String(value ?? '').toLowerCase().trim().replace(/\s+/g, ' ');

/**
 * All aliases for a store as a plain object keyed by normalized_alias:
 *   { [normalized_alias]: { itemId, alias, vendor_id, itemDescription } }
 * One query on item_aliases, then a batched inventory_items description
 * lookup merged in JS. Returns {} (with console.warn) when the migration is
 * not deployed.
 */
export async function fetchAliasMap(storeId, client = sharedSupabase) {
  if (!storeId) return {};
  const { data: aliases, error } = await client
    .from('item_aliases')
    .select(ALIAS_COLUMNS)
    .eq('store_id', storeId);
  if (error) {
    if (isMissingSchemaError(error)) {
      console.warn('[item-alias] item_aliases unavailable (migration not deployed?):', error.message);
      return {};
    }
    throw new Error(error.message);
  }

  const rows = (aliases || []).filter((row) => row.normalized_alias);
  const itemIds = [...new Set(rows.map((row) => row.inventory_item_id))]
    .filter((id) => id !== null && id !== undefined);
  if (!itemIds.length) return {};

  const { data: items, error: itemsError } = await client
    .from('inventory_items')
    .select('id, description')
    .in('id', itemIds);
  throwIfError(itemsError);

  const descriptionById = new Map((items || []).map((row) => [row.id, row.description]));
  const map = {};
  rows.forEach((row) => {
    map[row.normalized_alias] = {
      itemId: row.inventory_item_id,
      alias: row.alias,
      vendor_id: row.vendor_id ?? null,
      itemDescription: descriptionById.get(row.inventory_item_id) ?? null,
    };
  });
  return map;
}

/**
 * Pure lookup: entries whose normalized_alias contains the normalized query.
 * Queries shorter than 3 characters match nothing (substring noise). Returns
 * [{ itemId, alias, itemDescription }] in map insertion order.
 */
export function matchAliases(aliasMap, query) {
  const normalized = normalizeAlias(query);
  if (!aliasMap || normalized.length < 3) return [];
  const matches = [];
  Object.entries(aliasMap).forEach(([key, entry]) => {
    if (key.includes(normalized)) {
      matches.push({
        itemId: entry.itemId,
        alias: entry.alias,
        itemDescription: entry.itemDescription ?? null,
      });
    }
  });
  return matches;
}

/**
 * Records an alias for an item. Check-then-insert: an identical row (same
 * store + item + normalized alias) is returned as-is, and a concurrent
 * writer hitting the unique index (23505) is tolerated quietly (null).
 * Optional opts: { source, vendorId, confidence, approvedByUser } — source
 * maps to the alias_source column ('import' | 'vendor_product' | 'ai' |
 * 'user' | 'merge'; the DB defaults to 'user' when omitted).
 */
export async function addAlias(storeId, inventoryItemId, alias, opts = {}, client = sharedSupabase) {
  if (!storeId) throw new Error('storeId is required');
  if (!inventoryItemId) throw new Error('inventoryItemId is required');
  const normalized = normalizeAlias(alias);
  if (!normalized) return null;

  const { data: existing, error: lookupError } = await client
    .from('item_aliases')
    .select(ALIAS_COLUMNS)
    .eq('store_id', storeId)
    .eq('inventory_item_id', inventoryItemId)
    .eq('normalized_alias', normalized)
    .limit(1);
  throwIfError(lookupError);
  if (existing && existing.length) return existing[0];

  const row = {
    store_id: storeId,
    inventory_item_id: inventoryItemId,
    alias,
    normalized_alias: normalized,
  };
  if (opts.source !== undefined) row.alias_source = opts.source;
  if (opts.vendorId !== undefined) row.vendor_id = opts.vendorId;
  if (opts.confidence !== undefined) row.confidence = opts.confidence;
  if (opts.approvedByUser !== undefined) row.approved_by_user = opts.approvedByUser;

  const { data, error } = await client
    .from('item_aliases')
    .insert(row)
    .select()
    .single();
  if (error) {
    if (error.code === '23505' || /duplicate key/i.test(error.message || '')) return null;
    throw new Error(error.message);
  }
  return data;
}
